import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, RefreshCw, X } from 'lucide-react';
import { buildNavMenus, resolveHomePath, NavMenu } from '../config/navigation';
import { getStoredRepresentativeId, getStoredRoleName, getUserPermissions, hasPermission } from '../api/auth';
import { AUTH_USER_CHANGED_EVENT } from '../constants/auth-events';
import { usePortalMainNavFlag } from '../features/client-portal/usePortalMainNav';

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function MobileNavDrawer({ open, onClose }: MobileNavDrawerProps) {
  const location = useLocation();
  const portalMain = usePortalMainNavFlag();
  const [navMenus, setNavMenus] = useState<NavMenu[]>(() =>
    buildNavMenus(getUserPermissions(), code => hasPermission(code), {
      representativeId: getStoredRepresentativeId(),
      roleName: getStoredRoleName(),
      hideDesktopOnly: true,
    }),
  );
  const [expanded, setExpanded] = useState<string | null>(null);

  const refreshMenus = useCallback(() => {
    setNavMenus(buildNavMenus(getUserPermissions(), code => hasPermission(code), {
      representativeId: getStoredRepresentativeId(),
      roleName: getStoredRoleName(),
      hideDesktopOnly: true,
      portalMain,
    }));
  }, [portalMain]);

  useEffect(() => {
    refreshMenus();
    window.addEventListener(AUTH_USER_CHANGED_EVENT, refreshMenus);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, refreshMenus);
  }, [refreshMenus]);

  useEffect(() => {
    onClose();
    setExpanded(null);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const toggle = (label: string) => setExpanded(e => (e === label ? null : label));

  return (
    <div className="mobile-nav" data-testid="mobile-nav">
      <button
        type="button"
        className="mobile-nav__backdrop"
        aria-label="Закрыть меню"
        onClick={onClose}
      />
      <aside className="mobile-nav__panel" role="dialog" aria-modal="true" aria-label="Навигация">
        <div className="mobile-nav__header">
          <span className="mobile-nav__title">Меню</span>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Закрыть">
            <X size={20} />
          </button>
        </div>

        <nav className="mobile-nav__body">
          <NavLink
            to={resolveHomePath(getUserPermissions(), undefined, getStoredRoleName())}
            className={({ isActive }) => `mobile-nav__link${isActive ? ' mobile-nav__link--active' : ''}`}
            onClick={onClose}
          >
            Главная
          </NavLink>

          <button
            type="button"
            className="mobile-nav__link mobile-nav__link--action"
            onClick={() => { onClose(); window.location.reload(); }}
          >
            <RefreshCw size={14} />
            Обновить
          </button>

          <div className="mobile-nav__sep" />

          {navMenus.map(menu => {
            if (menu.to && !menu.items && !menu.groups) {
              return (
                <NavLink
                  key={menu.label}
                  to={menu.to}
                  className={({ isActive }) => `mobile-nav__link${isActive ? ' mobile-nav__link--active' : ''}`}
                  onClick={onClose}
                >
                  {menu.label}
                </NavLink>
              );
            }

            const isOpen = expanded === menu.label;
            return (
              <div key={menu.label} className="mobile-nav__section">
                <button
                  type="button"
                  className={`mobile-nav__section-btn${isOpen ? ' mobile-nav__section-btn--open' : ''}`}
                  onClick={() => toggle(menu.label)}
                  aria-expanded={isOpen}
                >
                  {menu.label}
                  <ChevronDown size={16} className={`mobile-nav__chevron${isOpen ? ' mobile-nav__chevron--open' : ''}`} />
                </button>

                {isOpen && (
                  <div className="mobile-nav__section-body">
                    {menu.items?.map((item, i) =>
                      item.sep ? (
                        <div key={i} className="mobile-nav__sep" />
                      ) : item.to ? (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            `mobile-nav__sublink${isActive ? ' mobile-nav__link--active' : ''}`
                          }
                          onClick={onClose}
                        >
                          {item.label}
                        </NavLink>
                      ) : null,
                    )}

                    {menu.groups?.map(group => (
                      <div key={group.groupLabel} className="mobile-nav__group">
                        <div className="mobile-nav__group-label">
                          <span>{group.icon}</span>
                          {group.groupLabel}
                        </div>
                        {group.items.map(item =>
                          item.to ? (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              className={({ isActive }) =>
                                `mobile-nav__sublink${isActive ? ' mobile-nav__link--active' : ''}`
                              }
                              onClick={onClose}
                            >
                              {item.label}
                            </NavLink>
                          ) : null,
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </div>
  );
}
